import type { ApiClient } from '@behuman.corp/contracts'

export type InsurancePayerOption = {
  id: string
  payerId: string
  payerName: string
  state?: string | undefined
  source: 'default' | 'custom'
  editable: boolean
  deletable: boolean
  parameterId?: string
}

export type InsurancePlanListItem = {
  id: string
  name: string
  productType: string
  lobCategory: string
  source: 'default' | 'custom'
  editable: boolean
  deletable: boolean
  parameterId?: string
}

export type InsurancePayerDetails = {
  id: string
  payerId: string
  payerName: string
  state: string
  source: 'default' | 'custom'
  editable: boolean
  deletable: boolean
  parameterId?: string
  plans: InsurancePlanListItem[]
}

export type UpsertInsurancePayerPayload = {
  payerParamId?: string
  payerId: string
  payerName: string
  state: string
}

export type UpsertInsurancePlanPayload = {
  payerBlockId: string
  planParamId?: string
  planName: string
  productType: string
  planId: string
  lobCategory: string
}

export async function getInsurancePayersList(client: ApiClient): Promise<InsurancePayerOption[]> {
  const response = await client.get('/insurance/payers-list')
  return response.result ?? []
}

/**
 * The shared default payer catalog, without any Organization's custom payers. It needs no
 * organization context, so it serves callers who act across Organizations (Provider
 * Operations), where `/insurance/payers-list` refuses anyone without exactly one.
 */
export async function getInsurancePayersCatalog(
  client: ApiClient,
): Promise<InsurancePayerOption[]> {
  const response = await client.get('/insurance/payers-catalog')
  return response.result ?? []
}

export async function getInsurancePayerDetails(
  client: ApiClient,
  payerBlockId: string,
): Promise<InsurancePayerDetails> {
  const response = await client.get('/insurance/payer-details/{payerBlockId}', {
    payerBlockId,
  })

  return response.result
}

export async function upsertInsurancePayer(
  client: ApiClient,
  payload: UpsertInsurancePayerPayload,
): Promise<{ parameterId: string }> {
  const response = await client.post('/insurance/payers/upsert', payload)
  return response.result
}

export async function upsertInsurancePlan(
  client: ApiClient,
  payload: UpsertInsurancePlanPayload,
): Promise<{ parameterId: string }> {
  const response = await client.post('/insurance/plans/upsert', payload)
  return response.result
}

export async function deleteInsurancePlan(client: ApiClient, planParamId: string): Promise<void> {
  await client.delete('/insurance/plans/{planParamId}', {
    planParamId,
  })
}

export async function deleteInsurancePayer(client: ApiClient, payerParamId: string): Promise<void> {
  await client.delete('/insurance/payers/{payerParamId}', {
    payerParamId,
  })
}
